import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Phone, Mail } from "lucide-react";
import logoText from "@/assets/logo-text.png";
import { PaymentMethods } from "./PaymentMethods";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const EMAIL_SUBSCRIBED_KEY = "email_subscribed";

export const LuxuryFooter = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from("email_subscribers")
        .insert({ email: email.trim(), source: "footer" });

      if (error) {
        if (error.code === "23505") {
          toast.success("You're already on our list!");
        } else {
          throw error;
        }
      } else {
        toast.success("Thanks for subscribing!");
        localStorage.setItem(EMAIL_SUBSCRIBED_KEY, "true");
      }
      setEmail("");
    } catch (error) {
      console.error("Error subscribing:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-muted/30 border-t border-border">
      <div className="container mx-auto px-4 py-16">
        {/* Email Signup Section */}
        <div className="max-w-xl mx-auto text-center mb-16">
          <h3 className="font-cormorant text-2xl md:text-3xl font-bold text-foreground mb-2">
            Get Tree Care Tips & Updates
          </h3>
          <p className="text-muted-foreground font-inter text-sm mb-4">
            Join our list for seasonal tips and exclusive offers.
          </p>
          <form onSubmit={handleSubmit} className="flex gap-2 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-11 font-inter flex-1"
            />
            <Button
              type="submit"
              disabled={loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-inter font-semibold h-11 px-6"
            >
              {loading ? "..." : "Subscribe"}
            </Button>
          </form>
        </div>

        {/* Three Column Layout */}
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* LEFT COLUMN - Business Info */}
          <div className="space-y-4">
            <img 
              src={logoText} 
              alt="Christmas Tree Food" 
              className="h-10" 
              width={91}
              height={40}
              loading="lazy"
            />
            <div className="text-sm text-muted-foreground font-inter leading-relaxed space-y-1">
              <p className="font-medium text-foreground">Christmas Tree Food</p>
              <p>23122 Alcalde Dr. Ste. E</p>
              <p>Laguna Hills, CA 92653</p>
            </div>
            <div className="space-y-2">
              <a 
                href="mailto:info@christmastreefood.com"
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-inter text-sm"
              >
                <Mail className="w-4 h-4" />
                info@christmastreefood.com
              </a>
              <a 
                href="tel:+19495256779"
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-inter text-sm"
              >
                <Phone className="w-4 h-4" />
                (949) 525-6779
              </a>
              <p className="text-muted-foreground font-inter text-sm">
                Mon–Fri, 9am–5pm PST
              </p>
            </div>
            <a 
              href="https://instagram.com/christmastreefood" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
              aria-label="@christmastreefood on Instagram"
            >
              <Instagram className="w-5 h-5" />
              <span className="text-sm font-inter">@christmastreefood</span>
            </a>
          </div>
          
          {/* CENTER COLUMN - Policies */}
          <div>
            <h3 className="font-cormorant text-xl font-bold text-foreground mb-4">Policies</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/policies/shipping" className="text-muted-foreground hover:text-primary transition-colors font-inter text-sm">
                  Shipping Policy
                </Link>
              </li>
              <li>
                <Link to="/policies/refund" className="text-muted-foreground hover:text-primary transition-colors font-inter text-sm">
                  Refund & Returns
                </Link>
              </li>
              <li>
                <Link to="/policies/terms" className="text-muted-foreground hover:text-primary transition-colors font-inter text-sm">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/policies/privacy" className="text-muted-foreground hover:text-primary transition-colors font-inter text-sm">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>
          
          {/* RIGHT COLUMN - Company */}
          <div>
            <h3 className="font-cormorant text-xl font-bold text-foreground mb-4">Company</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-muted-foreground hover:text-primary transition-colors font-inter text-sm">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors font-inter text-sm">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/wholesale" className="text-muted-foreground hover:text-primary transition-colors font-inter text-sm">
                  Wholesale
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-muted-foreground hover:text-primary transition-colors font-inter text-sm">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/customer-service" className="text-muted-foreground hover:text-primary transition-colors font-inter text-sm">
                  Customer Service
                </Link>
              </li>
              <li>
                <a 
                  href="https://lovable-project-2duat.myshopify.com/account" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors font-inter text-sm"
                >
                  Account
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="pb-8">
          <p className="text-center text-xs text-muted-foreground font-inter mb-4">
            Secure payments accepted
          </p>
          <PaymentMethods />
        </div>

        <div className="pt-8 border-t border-border">
          <p className="text-center text-sm text-muted-foreground font-inter">
            © {new Date().getFullYear()} Christmas Tree Food. A DBA of Igram, Inc. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};